import { useId, useMemo, useState, type ReactNode } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Connection, ShareEntry } from "@rago/web-shared";
import {
  acceptConnection,
  accessBaseline,
  accessChanges,
  connectionsKey,
  counterLine,
  countOn,
  deleteConnection,
  deleteServerAccess,
  fetchConnections,
  fromWire,
  invalidateSharing,
  isLibraryOn,
  itemCountLabel,
  libraryCounts,
  listedLibraries,
  patchGiven,
  personStatusLine,
  putServerAccess,
  sameAccess,
  selectionLine,
  serverCountLine,
  toWire,
  toggleLibrary,
  type Selection,
} from "../../../lib/sharing";
import { ApiProblemError } from "../../../lib/authManager";
import { useServers, type SettingsServer } from "../../../lib/servers";
import { useSources } from "../../../sources";
import { useNavServers } from "../../../lib/navServers";
import type { Library } from "../../../sources";
import { ErrorNote, InitialBadge, StatusDot, Switch, errorMessage } from "../../../components/ui";
import {
  SheetCard,
  SheetHeader,
  SheetInset,
  SheetLoading,
  SheetSection,
  UnsavedBar,
  useDraft,
  useLastTouched,
} from "../../../components/settings-sheet";
import { ConfirmDialog } from "../../../components/modal";
import {
  ArrowRightIcon,
  ChevronRightIcon,
  FilmstripIcon,
  TvIcon,
} from "../../../components/icons";
import { RequestCard } from "./sharing";

// A sibling of /settings/sharing (the underscore), not its child: the list
// is a page, not a layout.
export const Route = createFileRoute("/_authed/settings/sharing_/$userId")({
  component: SharingPerson,
});

function Crumb() {
  return (
    <SheetInset>
      <Link
        to="/settings/sharing"
        className="mt-6 flex w-fit cursor-pointer items-center gap-2 text-sm font-medium text-[var(--rago-text-3)] no-underline transition-colors select-none hover:text-[var(--rago-text-1)] focus-visible:ring-2 focus-visible:ring-[var(--rago-ring)] focus-visible:outline-none"
      >
        <ArrowRightIcon size={16} className="rotate-180" />
        Sharing
      </Link>
    </SheetInset>
  );
}

function SharingPerson() {
  const { userId } = Route.useParams();
  const connections = useQuery({ queryKey: connectionsKey, queryFn: fetchConnections });
  // Set the moment the connection ends from here: the page is on its way
  // back to the list and must not say "isn't connected" on the way.
  const [leaving, setLeaving] = useState(false);
  const connection = (connections.data ?? []).find((c) => c.user.userId === userId) ?? null;

  if (connections.isLoading || leaving) {
    return (
      <div>
        <Crumb />
        <SheetLoading />
      </div>
    );
  }
  if (!connection) {
    return (
      <div>
        <Crumb />
        <SheetHeader title="Sharing" />
        <SheetSection>
          <p className="text-[14.5px] text-[var(--rago-text-3)]">
            This person isn't connected with you.
          </p>
        </SheetSection>
      </div>
    );
  }
  // Keyed by the connection: the unsaved switches are this person's.
  return <Person key={connection.id} connection={connection} onLeave={() => setLeaving(true)} />;
}

/**
 * One person (Kenny's Claude Design "Sharing 1a", in the app's own
 * accent): who they are and where the connection stands, then EVERY
 * library of every server I own with a switch, then their restrictions
 * (Plex's Restrictions tab: "Allow downloads", one switch for the person,
 * applied to every server shared with them), then what they share with
 * me. The switches are a draft under the "Unsaved changes" pill (Kenny,
 * 2026-10-14: the pill, not saving as each one is flipped): Save sends the
 * servers that changed — the downloads flag rides each server's PUT —
 * Revert puts the switches back. Disconnect ends the connection both
 * ways; an invite of mine that still waits is cancelled here, and its
 * switches set what they get once they accept.
 */
function Person(props: { connection: Connection; onLeave: () => void }) {
  const c = props.connection;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { servers } = useServers();
  const { sources } = useSources();
  const nav = useNavServers(sources);
  const watchId = useId();
  const restrictionsId = useId();
  const sharesId = useId();

  const name = c.user.displayName || c.user.username;
  const theirRequest = c.status === "pending" && c.direction === "received";
  const myInvite = c.status === "pending" && c.direction === "sent";

  const owned = servers.filter((s) => s.owned);
  const ownedKey = owned.map((s) => s.id).join(",");
  // What is saved: one selection per server I own. Memoised on what it is
  // made from, so the draft only re-seeds when the saved state moves.
  const baseline = useMemo(
    () => accessBaseline(c.given, ownedKey ? ownedKey.split(",") : []),
    [c.given, ownedKey],
  );
  const [draft, setDraft] = useDraft(baseline, sameAccess);
  const form = draft ?? baseline;
  const changes = accessChanges(baseline, form);
  const dirty = changes.length > 0;
  const touched = useLastTouched();

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  function change(serverId: string, selection: Selection | null) {
    setDraft({ ...form, access: { ...form.access, [serverId]: selection } });
    setSaved(false);
    setSaveError(null);
  }

  function changeDownloads(allowDownloads: boolean) {
    setDraft({ ...form, allowDownloads });
    setSaved(false);
    setSaveError(null);
  }

  function revert() {
    setDraft(baseline);
    setSaveError(null);
  }

  /**
   * One request per server that changed (a server's share is replaced
   * whole, or dropped). Each answer goes straight into the connections
   * query, so a server that saved leaves the draft's differences at once
   * and one that was refused stays unsaved, named in the pill's note.
   */
  async function save() {
    if (saving || !dirty) return;
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    const refused: string[] = [];
    let why: unknown = null;
    for (const { serverId, selection, allowDownloads } of changes) {
      try {
        const wire = toWire(selection);
        let entry: ShareEntry | null = null;
        if (wire === null) await deleteServerAccess(c.id, serverId);
        else {
          entry = await putServerAccess(c.id, serverId, {
            libraryIds: wire,
            restrictions: { allowDownloads },
          });
        }
        qc.setQueryData<Connection[]>(connectionsKey, (old) =>
          old ? patchGiven(old, c.id, serverId, entry) : old,
        );
      } catch (err) {
        refused.push(owned.find((s) => s.id === serverId)?.name ?? "a server");
        why ??= err;
      }
    }
    setSaving(false);
    if (refused.length === 0) {
      setSaved(true);
      return;
    }
    // The broker's own words when it answered; a request that never got
    // there (the browser's "Failed to fetch") is just "Try again."
    const reason = why instanceof ApiProblemError ? errorMessage(why) : "Try again.";
    setSaveError(`Couldn't save ${refused.join(" and ")}. ${reason}`);
  }

  /** Disconnect, cancel my invite or decline theirs: one call, then the list. */
  async function end() {
    setBusy(true);
    setError(null);
    try {
      await deleteConnection(c.id);
      props.onLeave();
      qc.setQueryData<Connection[]>(connectionsKey, (old) => old?.filter((x) => x.id !== c.id));
      await navigate({ to: "/settings/sharing" });
      // Both directions went: what they shared leaves my server list too.
      void invalidateSharing(qc, { resources: true });
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      await acceptConnection(c.id);
      await invalidateSharing(qc, { resources: true });
    } catch (err) {
      setError(err);
      await invalidateSharing(qc);
    } finally {
      setBusy(false);
    }
  }

  const cards = owned.map((server) => ({
    server,
    libraries: listedLibraries(nav.data, server),
    selection:
      (server.id in form.access ? form.access[server.id] : baseline.access[server.id]) ?? null,
  }));
  // The downloads switch has a share to ride only once something is shared.
  const anyShared = cards.some((card) => card.selection !== null);
  let on = 0;
  let total = 0;
  for (const card of cards) {
    if (!card.libraries) continue;
    total += card.libraries.length;
    on += countOn(card.selection, card.libraries.map((l) => l.id)) ?? 0;
  }
  const count = counterLine(on, total);

  return (
    // .rago-sheet-fill: the root fills the sheet, so the pill docks at its foot.
    <div className="rago-sheet-fill @container [&>header]:pt-4">
      <Crumb />
      <SheetHeader
        leading={<InitialBadge name={name} size="lg" dashed={myInvite} />}
        title={name}
        lede={personStatusLine(c)}
        // On the title's row (not `action`): the status line below keeps the
        // full width, and on a phone the button wraps under the name.
        status={
          theirRequest ? undefined : (
            <button
              type="button"
              className="rago-btn rago-btn-danger rago-btn-md"
              onClick={() => {
                setError(null);
                setConfirming(true);
              }}
            >
              {myInvite ? "Cancel invite" : "Disconnect"}
            </button>
          )
        }
      />
      {error && !confirming ? (
        <SheetInset>
          <ErrorNote error={error} />
        </SheetInset>
      ) : null}
      <SheetSection>
        <div className="flex flex-col gap-8">
          {theirRequest ? (
            <RequestCard
              plain
              connection={c}
              counts={libraryCounts(nav.data, servers)}
              busy={busy}
              onAccept={() => void accept()}
              onDecline={() => void end()}
            />
          ) : null}

          {cards.length > 0 ? (
            <section aria-labelledby={watchId}>
              <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1.5">
                <h2 id={watchId} className="rago-caps">
                  {name} can watch
                </h2>
                <p className="text-[13px] text-[var(--rago-text-dim)]">
                  {theirRequest
                    ? "Accept their request to share your libraries."
                    : myInvite
                      ? [count, "theirs once they accept"].filter(Boolean).join(" · ")
                      : count}
                </p>
              </div>
              {/* onChange: the switch changed last gets the focus back when the pill leaves. */}
              <div className="flex flex-col gap-3" onChange={touched.onChange}>
                {cards.map(({ server, libraries, selection }) => (
                  <ServerAccessCard
                    key={server.id}
                    server={server}
                    libraries={libraries}
                    selection={selection}
                    locked={theirRequest}
                    onChange={(next) => change(server.id, next)}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {/* Their request has nothing of mine to restrict yet; my own
              invite does — the flag waits on the pending share with the
              libraries and reaches the grant when they accept. */}
          {cards.length > 0 && !theirRequest ? (
            <section aria-labelledby={restrictionsId}>
              <h2 id={restrictionsId} className="rago-caps mb-3">
                Restrictions
              </h2>
              <div onChange={touched.onChange}>
                <DownloadsCard
                  checked={form.allowDownloads}
                  enabled={anyShared}
                  onChange={changeDownloads}
                />
              </div>
            </section>
          ) : null}

          {c.status === "accepted" ? (
            <section aria-labelledby={sharesId}>
              <h2 id={sharesId} className="rago-caps mb-3">
                {name} shares with you
              </h2>
              {c.received.length === 0 ? (
                <p className="text-[14.5px] leading-normal text-[var(--rago-text-3)]">
                  Nothing yet. When {name} shares a library with you, it shows up here and in
                  Library.
                </p>
              ) : (
                <div className="flex flex-col gap-3">
                  {c.received.map((entry) => {
                    const server = servers.find((s) => s.id === entry.serverId);
                    return (
                      <SharedServerCard
                        key={entry.shareId}
                        entry={entry}
                        online={server?.online ?? false}
                        libraries={listedLibraries(nav.data, server)}
                      />
                    );
                  })}
                </div>
              )}
            </section>
          ) : null}
        </div>
      </SheetSection>

      <ConfirmDialog
        open={confirming}
        title={myInvite ? "Cancel invite" : `Disconnect ${name}?`}
        body={
          myInvite ? (
            <>Withdraw your invitation to {name}? You can invite them again later.</>
          ) : (
            <>
              This stops sharing in both directions. Their devices lose playback within a few
              minutes. You can connect again later.
            </>
          )
        }
        confirmLabel={myInvite ? "Cancel invite" : "Disconnect"}
        danger
        busy={busy}
        error={confirming ? error : undefined}
        onConfirm={() => void end()}
        onCancel={() => setConfirming(false)}
      />

      {/* The root's direct child (its dock rides at the sheet's foot), and
          always rendered, as UnsavedBar asks. */}
      <UnsavedBar
        dirty={dirty}
        saving={saving}
        saved={saved}
        error={saveError}
        onSave={() => void save()}
        onRevert={revert}
        onFocusLost={touched.focus}
      />
    </div>
  );
}

function LibraryIcon(props: { library: Library }) {
  const Icon = props.library.type === "show" ? TvIcon : FilmstripIcon;
  return <Icon size={16} className="shrink-0 text-[var(--rago-text-dim)]" />;
}

const ROW = "flex items-center gap-3 border-t border-[var(--rago-line-1)] px-[18px] py-[11px]";
/** A library's name with its count beside it — under it on a narrow sheet. */
const NAME_AND_COUNT =
  "flex min-w-0 flex-1 items-baseline gap-3 @max-[720px]:flex-col @max-[720px]:gap-1";
const COUNT = "min-w-0 truncate text-[13px] leading-tight text-[var(--rago-text-dim)]";
const QUIET_ROW = `${ROW} text-[13.5px] leading-normal text-[var(--rago-text-3)]`;

const DOWNLOADS_ID = "restrictions-allow-downloads";

/**
 * The person's restrictions: "Allow downloads" — save files and download
 * copies from the libraries I share with them. One switch for the person;
 * Save applies it to every server shared with them. With nothing shared
 * there is no share to carry it, so it rests and says so. (The card's
 * only row: no divider above it.)
 */
function DownloadsCard(props: {
  checked: boolean;
  enabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  const hintId = `${DOWNLOADS_ID}-hint`;
  return (
    <SheetCard>
      <div className="flex items-center gap-3 px-[18px] py-3.5">
        <div className="min-w-0 flex-1">
          <label
            htmlFor={DOWNLOADS_ID}
            className={`block text-[14.5px] leading-tight font-medium ${
              props.enabled ? "cursor-pointer" : ""
            }`}
          >
            Allow downloads
          </label>
          <p id={hintId} className="mt-1 text-[13px] leading-snug text-[var(--rago-text-dim)]">
            {props.enabled
              ? "Save files and download copies from the libraries you share"
              : "Share a library first"}
          </p>
        </div>
        <Switch
          id={DOWNLOADS_ID}
          checked={props.checked}
          disabled={!props.enabled}
          onChange={props.onChange}
          describedBy={hintId}
        />
      </div>
    </SheetCard>
  );
}

function ServerHead(props: {
  online: boolean;
  name: string;
  note: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 px-[18px] py-3.5">
      <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h3 className="flex items-center text-[14.5px] leading-tight font-semibold">
          <StatusDot online={props.online} />
          {props.name}
        </h3>
        <span className="text-[13px] leading-tight text-[var(--rago-text-dim)]">{props.note}</span>
      </div>
      {props.children}
    </div>
  );
}

/** One of my servers: every library it lists, each with its switch. A
 * server that cannot be asked right now keeps its all-or-nothing button. */
function ServerAccessCard(props: {
  server: SettingsServer;
  libraries: Library[] | null;
  selection: Selection | null;
  locked: boolean;
  onChange: (next: Selection | null) => void;
}) {
  const { server, libraries, selection } = props;
  const ids = libraries?.map((l) => l.id) ?? null;
  const everyOn =
    libraries === null
      ? selection === "*"
      : libraries.length > 0 && countOn(selection, ids) === libraries.length;
  const note =
    libraries !== null
      ? serverCountLine(selection, libraries.length)
      : server.online
        ? "Can't be reached from here"
        : "Offline";
  return (
    <SheetCard>
      <ServerHead online={server.online} name={server.name} note={note}>
        {libraries === null || libraries.length > 0 ? (
          <button
            type="button"
            disabled={props.locked}
            onClick={() => props.onChange(everyOn ? null : "*")}
            className="shrink-0 cursor-pointer rounded-[var(--rago-radius-s)] text-[13px] font-semibold text-[var(--rago-text-2)] transition-colors hover:text-[var(--rago-text-1)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--rago-ring)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {everyOn ? "Turn all off" : "Turn all on"}
          </button>
        ) : null}
      </ServerHead>
      {libraries === null ? (
        <p className={QUIET_ROW}>
          {selectionLine(selection)}. Libraries can't be listed while this server is{" "}
          {server.online ? "out of reach" : "offline"}.
        </p>
      ) : libraries.length === 0 ? (
        <p className={QUIET_ROW}>No libraries yet.</p>
      ) : (
        <ul>
          {libraries.map((lib) => {
            const id = `access-${server.id}-${lib.id}`;
            return (
              <li key={lib.id} className={ROW}>
                <LibraryIcon library={lib} />
                <div className={NAME_AND_COUNT}>
                  <label
                    htmlFor={id}
                    className={`min-w-0 truncate text-[14.5px] leading-tight font-medium ${
                      props.locked ? "" : "cursor-pointer"
                    }`}
                  >
                    {lib.title}
                  </label>
                  <span className={COUNT}>{itemCountLabel(lib)}</span>
                </div>
                <Switch
                  id={id}
                  checked={isLibraryOn(selection, lib.id)}
                  disabled={props.locked}
                  onChange={(checked) =>
                    props.onChange(toggleLibrary(selection, lib.id, checked, ids ?? []))
                  }
                />
              </li>
            );
          })}
        </ul>
      )}
    </SheetCard>
  );
}

/** One of THEIR servers, read-only: what I can open there. A library is a
 * link into Library once the server answers from here. */
function SharedServerCard(props: {
  entry: ShareEntry;
  online: boolean;
  libraries: Library[] | null;
}) {
  const { entry, libraries } = props;
  const selection = fromWire(entry.libraryIds);
  const shown = libraries?.filter((l) => isLibraryOn(selection, l.id)) ?? null;
  const note =
    shown === null
      ? props.online
        ? "Can't be reached from here"
        : "Offline"
      : shown.length === 1
        ? "1 library"
        : `${shown.length} libraries`;
  return (
    <SheetCard>
      <ServerHead online={props.online} name={entry.serverName} note={note} />
      {shown === null ? (
        <p className={QUIET_ROW}>{selectionLine(selection)}.</p>
      ) : shown.length === 0 ? (
        // Shared a moment ago: the server learns of it at its next check-in.
        <p className={QUIET_ROW}>
          {selectionLine(selection)}. They show up here within a few minutes.
        </p>
      ) : (
        <ul>
          {shown.map((lib) => (
            <li key={lib.id} className="border-t border-[var(--rago-line-1)]">
              <Link
                to="/library/$sourceId/$libraryId"
                params={{ sourceId: entry.serverId, libraryId: lib.id }}
                className="group flex items-center gap-3 px-[18px] py-[11px] text-inherit no-underline transition-colors hover:bg-white/[0.03] focus-visible:shadow-[inset_0_0_0_2px_var(--rago-ring)] focus-visible:outline-none"
              >
                <LibraryIcon library={lib} />
                <div className={NAME_AND_COUNT}>
                  <span className="min-w-0 truncate text-[14.5px] leading-tight font-medium">
                    {lib.title}
                  </span>
                  <span className={COUNT}>{itemCountLabel(lib)}</span>
                </div>
                <ChevronRightIcon
                  size={16}
                  className="shrink-0 text-[var(--rago-text-dim)] transition-colors group-hover:text-[var(--rago-text-1)]"
                />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </SheetCard>
  );
}
